import Page from "components/Page";
import { Button } from "components/commons/Button";
import { Divider } from "components/commons/Divider";
import Icon from "components/commons/Icon";
import Input from "components/commons/Input";
import { Link } from "components/commons/Link";
import Layout from "components/layouts/Layout";
import { ProductInterval, plus, standard } from "data/products";
import { GetStaticProps } from "next";
import { useTranslations } from "next-intl";
import { ReactElement, useState } from "react";
import CheckCircle from "@/assets/img/icons/check-circle-green.svg";
import MinusCircle from "@/assets/img/icons/minus-circle.svg";
import Square from "@/assets/img/icons/square.svg";

const Pricing = () => {
  const t = useTranslations("planos-e-precos");

  const [productUsers, setProductUsers] = useState(2);
  const [productInterval, setProductInterval] = useState(ProductInterval.YEAR);

  const features = [
    {
      id: 1,
      name: t("feature-1"),
      standard: false,
      plus: true,
      pro: true,
    },
    {
      id: 2,
      name: t("feature-2"),
      standard: true,
      plus: true,
      pro: true,
    },
    {
      id: 3,
      name: t("feature-3"),
      standard: true,
      plus: true,
      pro: true,
    },
    {
      id: 4,
      name: t("feature-4"),
      standard: true,
      plus: true,
      pro: true,
    },
    {
      id: 5,
      name: t("feature-5"),
      standard: false,
      plus: true,
      pro: true,
    },
    {
      id: 6,
      name: t("feature-6"),
      standard: false,
      plus: true,
      pro: true,
    },
    {
      id: 7,
      name: t("feature-7"),
      standard: false,
      plus: true,
      pro: true,
    },
    {
      id: 8,
      name: t("feature-8"),
      standard: false,
      plus: true,
      pro: true,
    },
    {
      id: 9,
      name: t("feature-9"),
      standard: false,
      plus: true,
      pro: true,
    },
    {
      id: 10,
      name: t("feature-10"),
      standard: true,
      plus: true,
      pro: true,
    },
  ];

  const onProductIntervalChange = (e: any) => {
    setProductInterval(e.target.value);
  };

  const onProductUsersChange = (e: any) => {
    if (!e.target.value || (e.target.value > 0 && e.target.value <= 100))
      setProductUsers(e.target.value);
  };

  return (
    <Page
      id="pricing"
      title={t("metadata-title")}
      description={t("metadata-description")}
      className="bg-blue-light md:bg-pricing"
      footer
    >
      <h1 className="text-7xl text-white md:text-10xl font-bold md:font-extrabold text-center">
        {t("title")}
      </h1>
      <Divider className="bg-white" />

      <div className="flex flex-col items-center text-center justify-center gap-6 lg:text-left lg:grid lg:grid-cols-[max-content_max-content]">
        <label className="text-xl text-white font-bold m-0">
          {t("how-many-users")}
        </label>
        <Input
          id="users"
          type="number"
          variant="primary"
          className="text-blue text-lg font-bold w-20 bg-white border-b-2 border-white border-2"
          value={productUsers}
          onChange={onProductUsersChange}
          min={2}
          max={100}
        />
        <label className="text-xl text-white font-bold m-0">
          {t("which-payment-interval")}
        </label>
        <div className="flex space-x-4 items-center md:ml-1">
          <Input
            id="productIntervalQuarterly"
            type="radio"
            labelClassName="text-white"
            variant="primary"
            label="Trimestral"
            value={ProductInterval.QUARTER}
            checked={productInterval === ProductInterval.QUARTER}
            onChange={onProductIntervalChange}
          />
          <Input
            id="productIntervalYearly"
            type="radio"
            labelClassName="text-white"
            variant="primary"
            label="Anual"
            value={ProductInterval.YEAR}
            checked={productInterval === ProductInterval.YEAR}
            onChange={onProductIntervalChange}
          />
        </div>
      </div>

      <div className="relative mx-auto max-w-4xl mt-8 grid grid-cols-2 gap-10">
        <div className="relative col-span-2 md:col-span-1">
          <Icon
            src={Square}
            height={64}
            width={64}
            className="absolute top-16 left-12 rotate-45 opacity-10 lg:left-14"
            alt="Standard"
          />
          <div className="bg-white px-4 py-16 md:px-10 md:py-20 text-center rounded-lg shadow-2xl">
            <div className="inline-block text-left">
              <div className="relative text-center">
                <h4 className="text-6xl font-extrabold">Standard</h4>
                <div>
                  <label className="text-10xl font-extrabold mt-8 mb-0 text-blue">
                    {standard.getPaymentInformation(
                      productUsers,
                      productInterval,
                    ).amountPerMonth /
                      100 +
                      "€"}
                  </label>
                  <label className="ml-2 text-lg text-black font-bold">
                    {t("per-month")}
                  </label>
                </div>
                <label className="text-md font-bold">
                  {t("price-detail", {
                    price:
                      standard.getPaymentInformation(
                        productUsers,
                        productInterval,
                      ).totalAmount / 100,
                    productInterval:
                      productInterval === ProductInterval.QUARTER ? 3 : 12,
                    productUsers: Math.max(productUsers, 2),
                  })}
                </label>
                <label className="text-md font-semibold italic">
                  {t("tax-not-included")}
                </label>
              </div>
              <div className="flex flex-col text-center space-y-4 mt-4 mb-8">
                <Link
                  href={`/comprar?product_type=standard&product_users=${productUsers}&product_interval=${productInterval}`}
                  prefetch={false}
                >
                  <Button variant="primary" className="w-full">
                    {t("buy")}
                  </Button>
                </Link>
                <Link
                  href="/download"
                  variant="primary"
                  className="mt-4 font-extrabold"
                  prefetch={false}
                  text={t("try-for-free")}
                ></Link>
              </div>
              <div className="text-center">
                <div className="inline-block text-left space-y-4">
                  {features.map(({ id, name, standard }: any) => (
                    <div key={id}>
                      <div className="inline-flex space-x-10 align-middle">
                        <Icon
                          key={id}
                          src={standard ? CheckCircle : MinusCircle}
                          width={20}
                          height={20}
                          className="m-1 mr-3 h-6 w-6"
                          alt={name}
                        />
                        {name}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="relative col-span-2 md:col-span-1">
          <Icon
            src={Square}
            height={64}
            width={64}
            className="absolute top-16 left-12 rotate-45 opacity-10 lg:left-24"
            alt="Plus"
          />
          <div className="bg-white px-4 py-16 md:px-10 md:py-20 text-center rounded-lg shadow-2xl">
            <div className="inline-block text-left">
              <div className="relative text-center">
                <h4 className="text-6xl font-extrabold">Plus</h4>
                <div>
                  <label className="text-10xl font-extrabold mt-8 mb-0 text-blue">
                    {plus.getPaymentInformation(productUsers, productInterval)
                      .amountPerMonth /
                      100 +
                      "€"}
                  </label>
                  <label className="ml-2 text-lg text-black font-bold">
                    {t("per-month")}
                  </label>
                </div>
                <label className="text-md font-bold">
                  {t("price-detail", {
                    price:
                      plus.getPaymentInformation(productUsers, productInterval)
                        .totalAmount / 100,
                    productInterval:
                      productInterval === ProductInterval.QUARTER ? 3 : 12,
                    productUsers: Math.max(productUsers, 2),
                  })}
                </label>
                <label className="text-md font-semibold italic">
                  {t("tax-not-included")}
                </label>
              </div>
              <div className="flex flex-col text-center space-y-4 mt-4 mb-8">
                <Link
                  href={`/comprar?product_type=plus&product_users=${productUsers}&product_interval=${productInterval}`}
                  prefetch={false}
                >
                  <Button variant="primary" className="w-full">
                    {t("buy")}
                  </Button>
                </Link>
                <Link
                  href="/download"
                  variant="primary"
                  className="mt-4 font-extrabold"
                  prefetch={false}
                  text={t("try-for-free")}
                ></Link>
              </div>
              <div className="text-center">
                <div className="inline-block text-left space-y-4">
                  {features.map(({ id, name, plus }: any) => (
                    <div key={id}>
                      <div className="inline-flex space-x-10 align-middle">
                        <Icon
                          key={id}
                          src={plus ? CheckCircle : MinusCircle}
                          width={20}
                          height={20}
                          className="m-1 mr-3 h-6 w-6"
                          alt={name}
                        />
                        {name}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Page>
  );
};

export const getStaticProps: GetStaticProps = async (context) => {
  return {
    props: {
      context,
      messages: (await import(`../lang/${context.locale}.json`)).default,
    },
  };
};

Pricing.getLayout = function getLayout(page: ReactElement) {
  return <Layout variant="secondary">{page}</Layout>;
};

export default Pricing;
